/**
 * QrScreen — Desktop Fallback / Mobile Handoff
 *
 * Shown when the app is opened on a desktop browser and
 * brand.allowDesktop === false (or not set). Displays a QR code encoding
 * the current URL (minus UTM params) so the user can scan it with their
 * phone and continue in the mobile experience.
 *
 * Layout (all within h-dvh — footer rendered by App below fold):
 *   ┌────────────────────────────────────────────────┐
 *   │ HEADER  (bg-black, h-20)           [Logo]      │
 *   ├────────────────────────────────────────────────┤
 *   │                                                │
 *   │   [Title]                ┌──────────────────┐  │
 *   │   [Subtitle]             │    QR CODE SVG   │  │
 *   │   [Erneut laden btn]     └──────────────────┘  │
 *   │                                                │
 *   └────────────────────────────────────────────────┘
 *   ┌────────────────────────────────────────────────┐
 *   │ FOOTER (below fold — scroll to see)            │
 *   └────────────────────────────────────────────────┘
 *
 * @see docs/ims-brand-frontends/08-ui-screens.md#screen-1--qr-screen-desktop-fallback
 */
import { useMemo } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import type { ResolvedConfig } from '@ims/shared';
import { BrandButton } from '../ui/BrandButton';
import { generateQrUrl } from '../../utils/generateQrUrl';

interface QrScreenProps {
  config?: ResolvedConfig | null;
}

export function QrScreen({ config }: QrScreenProps) {
  // Generate QR target URL once on mount — no need to recompute on re-renders
  const qrUrl = useMemo(() => generateQrUrl(), []);

  const ui     = config?.ui ?? {};
  const assets = config?.brand.assets ?? {};

  const title       = ui.qrTitle        ?? 'Immersive Studio';
  const subtitle    = ui.qrSubtitle     ?? 'Scan the QR code to experience the model on your mobile device.';
  const reloadLabel = ui.qrReloadButton ?? 'Reload';
  const logoLinkUrl = config?.market.logoLinkUrl ?? '#';
  const logoUrl     = assets.logoUrl;

  return (
    <div className="h-full flex flex-col bg-brand-surface-dark">

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <header className="h-20 shrink-0 bg-black flex items-center justify-end px-5">
        <a
          href={logoLinkUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={config?.brand.displayName ?? 'Brand'}
        >
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={config?.brand.displayName ?? 'Brand logo'}
              className="h-16 w-auto"
              draggable={false}
            />
          ) : (
            /* CSS-variable fallback when no logoUrl is configured */
            <span
              className="block h-8 w-16 bg-contain bg-no-repeat bg-right"
              style={{ backgroundImage: 'var(--icon-logo)' }}
              aria-hidden="true"
            />
          )}
        </a>
      </header>

      {/* ── Main content — centered, two-column ─────────────────────────── */}
      <div className="flex-1 flex items-center justify-center gap-18 px-12 py-8">

        {/* Left column — text + reload CTA */}
        <div className="flex flex-col gap-6 max-w-sm">
          <h1 className="text-3xl font-bold text-brand-text leading-tight">
            {title}
          </h1>
          <p className="text-sm text-brand-text-secondary leading-relaxed">
            {subtitle}
          </p>
          <BrandButton
            className="max-w-45"
            onClick={() => window.location.reload()}
          >
            {reloadLabel}
          </BrandButton>
        </div>

        {/* Right column — QR code in white-bordered rounded container */}
        <div className="shrink-0 p-3.75 border-2 border-white rounded-xl inline-flex items-center justify-center">
          <QRCodeSVG
            value={qrUrl}
            size={250}
            fgColor="#ffffff"
            bgColor="transparent"
            level="M"
          />
        </div>

      </div>
    </div>
  );
}
